import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAccount, useSignTypedData } from 'wagmi';
import { isAddress, getAddress } from 'ethers';
import { Loader2, Signature, Wallet, Users } from 'lucide-react';
import { useWallet } from '@/context/WalletContext';
import { newNonce, shortHex } from '@/lib/bindingConsent';
import CheckList from './CheckList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/binding`;

export default function PrincipalsEditor({ kypaId, wallet, onSaved }) {
  const { address, isConnected } = useAccount();
  const { connectWallet } = useWallet();
  const { signTypedDataAsync } = useSignTypedData();
  const [cfg, setCfg] = useState(null);
  const [current, setCurrent] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => axios.get(`${API}/principals/${encodeURIComponent(kypaId)}`).then((r) => setCurrent(r.data)), [kypaId]);
  useEffect(() => { axios.get(`${API}/principals-config`).then((r) => setCfg(r.data)); load(); }, [load]);

  const entries = text.split(/[\s,]+/).filter(Boolean);
  const invalid = entries.filter((a) => !isAddress(a));
  const isOperator = isConnected && address?.toLowerCase() === wallet?.toLowerCase();
  const ready = cfg && invalid.length === 0 && entries.length <= cfg.max_principals;

  const sign = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const now = Math.floor(Date.now() / 1000);
      const message = { kypaId, operator: address, principals: entries.map((a) => getAddress(a)), issuedAt: now, nonce: newNonce() };
      const signature = await signTypedDataAsync({ domain: cfg.domain, types: cfg.types, primaryType: cfg.primary_type, message: { ...message, issuedAt: BigInt(now), nonce: BigInt(message.nonce) } });
      const res = (await axios.post(`${API}/principals`, { message, signature })).data;
      setResult(res);
      if (res.accepted) { load(); onSaved?.(); }
    } catch (e) {
      setError(e?.shortMessage || e?.response?.data?.detail || e?.message || 'Signing failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-black/30 p-4 space-y-3" data-testid="principals-editor">
      <div className="flex items-center gap-2 text-sm font-semibold text-white"><Users className="w-4 h-4 text-red-300" /> Who may authorize this agent</div>
      <p className="text-[11px] text-gray-500 leading-relaxed">Only wallets on this list can sign consent receipts for this agent. Until the operator signs a list, the gate refuses every real receipt.</p>
      <div className="text-xs" data-testid="principals-current">
        {!current ? '...' : current.set
          ? (current.count ? <span className="text-gray-300" data-testid="principals-count">{current.count} authorized wallet{current.count === 1 ? '' : 's'} (kept private)</span> : <span className="text-amber-300">Empty list: every real receipt is refused</span>)
          : <span className="text-amber-300" data-testid="principals-not-set">No list set: every real receipt is refused</span>}
      </div>
      {isOperator ? (
        <>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="One wallet address per line. Signing replaces the whole list." className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs font-mono text-gray-200" data-testid="principals-input" />
          {invalid.length > 0 && <p className="text-[11px] text-red-400" data-testid="principals-invalid">Not a valid address: {invalid[0]}</p>}
          <button type="button" onClick={sign} disabled={!ready || busy} className="inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-40 px-4 py-2 text-xs font-semibold text-white" data-testid="principals-sign">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Signature className="w-3.5 h-3.5" />} Sign list with operator wallet
          </button>
        </>
      ) : (
        <button type="button" onClick={connectWallet} className="inline-flex items-center gap-2 text-xs font-semibold text-gray-300 border border-white/15 rounded-full px-3 py-1.5" data-testid="principals-connect">
          <Wallet className="w-3.5 h-3.5" /> {isConnected ? `Connected ${shortHex(address)} is not the operator wallet` : 'Operator: connect wallet to edit'}
        </button>
      )}
      {error && <p className="text-xs text-red-400" data-testid="principals-error">{error}</p>}
      {result && <CheckList checks={result.checks} testid="principals-checks" />}
    </div>
  );
}
